// CV (Lebenslauf) data model. Mirrors discovery's CvDto: a CurriculumVitae
// row stores a `configuration` (template + styling) and a `content` (the
// resolved CV sections) as JSON `Object` scalars. The PDF is rendered
// client-side (@react-pdf/renderer) from these two - genesis has no
// server-side generateCv.

export const CV_API_VERSION = 1;

// Template slots. 6 (Minimal) was dropped; 11 is the Comic-Sans easter egg.
// Display order = "Variante N" numbering: colorful layouts first, plainer ones
// last, easter egg at the very end.
export const CV_TEMPLATE_IDS = [2, 3, 5, 10, 7, 1, 8, 9, 11] as const;
export type CvTemplateId = (typeof CV_TEMPLATE_IDS)[number];

export const CV_FONTS = ["Noto Sans", "Noto Serif", "Roboto"] as const;
export type CvFont = (typeof CV_FONTS)[number];

// Editable styling ranges (discovery's sliders).
export const CV_RANGES = {
  textFontSize: { min: 8, max: 16, default: 10 },
  captionFontSize: { min: 8, max: 20, default: 13 },
  sectionMargin: { min: 5, max: 30, default: 12 },
  sectionIndentation: { min: 0, max: 30, default: 0 },
  sectionSpacing: { min: 0, max: 30, default: 8 },
} as const;

export interface CvConfiguration {
  templateIdentifier: CvTemplateId;
  color: string; // heading/accent color, hex
  fontFamily: CvFont;
  textFontSize: number;
  captionFontSize: number;
  sectionMargin: number;
  sectionIndentation: number;
  sectionSpacing: number;
}

export interface CvContact {
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  birthDate?: string;
  nationality?: string;
  drivingLicence?: string;
}

export interface CvExperience {
  jobTitle?: string;
  employer?: string;
  department?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  activities?: string[];
}

export interface CvEducation {
  institution?: string;
  field?: string;
  degree?: string;
  city?: string;
  country?: string;
  startDate?: string;
  endDate?: string;
}

export interface CvCertification {
  name?: string;
  date?: string;
}

export interface CvLanguage {
  language?: string;
  level?: string;
}

export interface CvExpertiseItem {
  description?: string;
  level?: string;
}

export interface CvCustomSection {
  id: string;
  title: string;
  text: string;
}

// Toggleable / orderable standard sections.
export const CV_SECTION_KEYS = [
  "summary",
  "experience",
  "education",
  "expertise",
  "languages",
  "certifications",
] as const;
export type CvSectionKey = (typeof CV_SECTION_KEYS)[number];

export interface CvContent {
  header: { firstName?: string; lastName?: string; role?: string };
  contact: CvContact;
  image?: string | null; // base64 data URL or null
  summary?: string;
  experiences: CvExperience[];
  educations: CvEducation[];
  certifications: CvCertification[];
  languages: CvLanguage[];
  expertise: CvExpertiseItem[];
  custom: CvCustomSection[];
  // Standard sections to render, in order.
  sections: CvSectionKey[];
}

export function defaultCvConfiguration(): CvConfiguration {
  return {
    templateIdentifier: 2, // new "Variante 1"
    color: "#1f2937",
    fontFamily: "Noto Sans",
    textFontSize: CV_RANGES.textFontSize.default,
    captionFontSize: CV_RANGES.captionFontSize.default,
    sectionMargin: CV_RANGES.sectionMargin.default,
    sectionIndentation: CV_RANGES.sectionIndentation.default,
    sectionSpacing: CV_RANGES.sectionSpacing.default,
  };
}

export function emptyCvContent(): CvContent {
  return {
    header: {},
    contact: {},
    image: null,
    summary: "",
    experiences: [],
    educations: [],
    certifications: [],
    languages: [],
    expertise: [],
    custom: [],
    sections: [...CV_SECTION_KEYS],
  };
}

// The CurriculumVitae `configuration`/`content` fields are the `Object` scalar
// (Record<string, unknown>) on the wire. These helpers narrow at that seam,
// filling defaults so older/partial rows still render.
export function parseCvConfiguration(raw: unknown): CvConfiguration {
  const o = (raw ?? {}) as Partial<CvConfiguration>;
  const d = defaultCvConfiguration();
  const tpl = Number(o.templateIdentifier);
  return {
    ...d,
    ...o,
    templateIdentifier: (CV_TEMPLATE_IDS as readonly number[]).includes(tpl)
      ? (tpl as CvTemplateId)
      : d.templateIdentifier,
    fontFamily: (CV_FONTS as readonly string[]).includes(o.fontFamily ?? "")
      ? (o.fontFamily as CvFont)
      : d.fontFamily,
  };
}

export function parseCvContent(raw: unknown): CvContent {
  const o = (raw ?? {}) as Partial<CvContent>;
  const e = emptyCvContent();
  return {
    ...e,
    ...o,
    header: { ...e.header, ...(o.header ?? {}) },
    contact: { ...e.contact, ...(o.contact ?? {}) },
    experiences: o.experiences ?? e.experiences,
    educations: o.educations ?? e.educations,
    certifications: o.certifications ?? e.certifications,
    languages: o.languages ?? e.languages,
    expertise: o.expertise ?? e.expertise,
    custom: o.custom ?? e.custom,
    sections: o.sections?.length ? o.sections : e.sections,
  };
}
